import { motion as Motion } from 'framer-motion';

const brandLogoUrl = '/logo.svg';

function imageWidthClass(variant) {
  if (variant === 'hero') {
    return 'w-[min(100%,32rem)]';
  }

  if (variant === 'footer') {
    return 'w-[min(100%,18rem)]';
  }

  return 'w-[min(100%,14rem)]';
}

function captionAlignClass(align) {
  if (align === 'left') {
    return 'text-left';
  }

  if (align === 'right') {
    return 'text-right';
  }

  return 'text-center';
}

function lockupAlignClass(align) {
  if (align === 'left') {
    return 'items-start';
  }

  if (align === 'right') {
    return 'items-end';
  }

  return 'items-center';
}

export function SDMonogram({
  className = '',
  compact = false,
  framed = true,
  watermark = false,
  rings = true,
}) {
  const shellClass = framed
    ? 'rounded-[1.15rem] border border-[rgba(56,189,248,0.2)] bg-[linear-gradient(180deg,rgba(14,30,69,0.34)_0%,rgba(5,10,31,0.66)_100%)] shadow-[0_18px_44px_-28px_rgba(56,189,248,0.32)] backdrop-blur-xl'
    : 'bg-transparent shadow-none';
  const scaleClass = compact ? 'scale-[1.42]' : watermark ? 'scale-[1.56]' : 'scale-[1.48]';
  const translateClass = compact ? '-translate-x-[1%]' : watermark ? '-translate-x-[2%]' : '-translate-x-[1.5%]';

  return (
    <Motion.div
      aria-hidden="true"
      className={`relative isolate overflow-hidden ${shellClass} ${className}`}
      animate={{ y: [0, -3, 0] }}
      transition={{ duration: compact ? 6.4 : 8.2, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
    >
      {framed ? (
        <>
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(191,248,255,0.1)_0%,transparent_28%,transparent_76%,rgba(56,189,248,0.08)_100%)]" />
          <div className="pointer-events-none absolute inset-px rounded-[inherit] border border-white/8" />
          <Motion.div
            className="pointer-events-none absolute inset-y-0 left-[-28%] w-[24%] bg-[linear-gradient(90deg,transparent_0%,rgba(191,248,255,0.22)_50%,transparent_100%)]"
            animate={{ x: ['0%', '500%'] }}
            transition={{ duration: 8.4, repeat: Number.POSITIVE_INFINITY, ease: 'linear' }}
          />
        </>
      ) : null}

      {rings ? (
        <>
          <Motion.div
            className="pointer-events-none absolute inset-[8%] rounded-full border border-[rgba(56,189,248,0.1)]"
            animate={{ rotate: 360 }}
            transition={{ duration: 18, repeat: Number.POSITIVE_INFINITY, ease: 'linear' }}
          />
          <Motion.div
            className="pointer-events-none absolute inset-[18%] rounded-full border border-[rgba(191,248,255,0.12)]"
            animate={{ rotate: -360, scale: [0.98, 1.04, 1] }}
            transition={{
              rotate: { duration: 11.4, repeat: Number.POSITIVE_INFINITY, ease: 'linear' },
              scale: { duration: 4.2, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' },
            }}
          />
        </>
      ) : null}

      <div className="pointer-events-none absolute inset-[12%] rounded-full bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.22)_0%,transparent_70%)] blur-2xl" />
      <img
        src={brandLogoUrl}
        alt=""
        className={`pointer-events-none absolute left-0 top-1/2 h-[112%] max-w-none -translate-y-1/2 ${scaleClass} ${translateClass}`}
      />
    </Motion.div>
  );
}

export function BrandWordmark({
  brand = 'Sanjivani DevWorks',
  className = '',
  align = 'center',
  variant = 'panel',
  caption,
}) {
  return (
    <div className={`relative flex flex-col ${lockupAlignClass(align)} ${className}`}>
      <div className="pointer-events-none absolute inset-x-0 top-[20%] h-12 bg-[radial-gradient(ellipse_at_center,rgba(56,189,248,0.18)_0%,transparent_72%)] blur-2xl" />
      <div className="relative overflow-hidden">
        <img
          src={brandLogoUrl}
          alt={brand}
          className={`${imageWidthClass(variant)} max-w-none origin-left scale-[1.18] translate-x-[24%]`}
        />
      </div>
      {caption ? (
        <div className={`mt-3 text-[0.56rem] font-mono uppercase tracking-[0.3em] text-white/46 ${captionAlignClass(align)}`}>
          {caption}
        </div>
      ) : null}
    </div>
  );
}

export function BrandLockup({
  brand = 'Sanjivani DevWorks',
  className = '',
  align = 'left',
  variant = 'hero',
  caption,
}) {
  return (
    <Motion.div
      className={`relative flex flex-col ${lockupAlignClass(align)} ${className}`}
      animate={{ y: [0, -2, 0] }}
      transition={{ duration: 7.6, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-[18%] h-16 bg-[radial-gradient(ellipse_at_center,rgba(56,189,248,0.22)_0%,transparent_72%)] blur-2xl" />
      <div className="relative">
        <img
          src={brandLogoUrl}
          alt={brand}
          className={`${imageWidthClass(variant)} h-auto drop-shadow-[0_18px_26px_rgba(3,8,21,0.48)]`}
        />
      </div>
      {caption ? (
        <div className={`mt-3 text-[0.56rem] font-mono uppercase tracking-[0.3em] text-white/46 ${captionAlignClass(align)}`}>
          {caption}
        </div>
      ) : null}
    </Motion.div>
  );
}

export function BrandWatermark({
  className = '',
  framed = false,
}) {
  return (
    <Motion.div
      aria-hidden="true"
      className={`pointer-events-none absolute ${className}`}
      animate={{ opacity: [0.08, 0.16, 0.1], scale: [0.98, 1.02, 1] }}
      transition={{ duration: 9.2, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
    >
      <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.14)_0%,transparent_72%)] blur-3xl" />
      <SDMonogram
        framed={framed}
        watermark
        rings={false}
        className="h-full w-full opacity-100"
      />
    </Motion.div>
  );
}
